/**
 * facesheet.js — THE ONE ANSWER TO "WHO IS THIS PATIENT" ON A PRINTED DOCUMENT.
 * v242.145.0 · PVS-FACESHEET-CLIENT-SOURCE-1 / PVS-EMPTY-IS-NOT-DENIED-1
 *
 * ═══ WHY THIS EXISTS ═══
 * The pre-visit hand-off had TWO data paths for one patient. The BODY came from
 * the API, which re-reads `profiles` + `users` on every call — which is where
 * "42-year-old male" in the Clinical Snapshot came from. The FACESHEET on the
 * same page was rebuilt here from `useProfileStore`, which is sessionStorage
 * backed and is empty after an iOS WKWebView content-process recycle (the one
 * the SHARE SHEET itself triggers — see utils/authStorage.js) or after a single
 * failed GET /profile at boot. A real document went out saying "42-year-old
 * male" four inches under "Sex: Not provided".
 *
 * ★★★ THE RULING (Dr. Rami, 2026-08-27): AN EMPTY IDENTITY SHOULD BE
 *     IMPOSSIBLE, not politely handled. So the server is the source, this
 *     module is the only shape either source is allowed to arrive in, and a
 *     document with no identity is REFUSED rather than printed.
 *
 * ═══ AND THE HALF THAT WAS WORSE THAN A BLANK ═══
 * The store defaults every section to `{}` — an OBJECT, so truthy — and every
 * guard in the generator was `if (!section) return 'Not documented'`. An empty
 * object sailed through it and fell to the bottom of each formatter, which is
 * where the AFFIRMATIVE NEGATIVES live: `None reported` for past medical and
 * surgical history, `Non-contributory` for family and social. So an UNLOADED
 * profile did not print blanks. It told the receiving physician the patient
 * denied everything — on the same page as the printed promise
 * "Stated only where the patient was asked — not assumed normal."
 *
 * ★★★ THEREFORE: A NEGATIVE IS A CLAIM AND MUST BE EARNED. `sectionState`
 *     returns 'absent' unless the section is genuinely present, and a formatter
 *     may print an affirmative negative ONLY on the patient's own explicit flag
 *     (`noConditions`, `noSurgeries`, `hasMedications === false`,
 *     `noKnownAllergies`, `completed === true`). Absence of a list is NOT a
 *     denial. It never was — the code just could not tell the difference.
 *
 * ★★ ONE PARSER, ONE SHAPE. Both sources normalise through here, so the
 *    renderer has exactly one input shape and cannot grow a second opinion
 *    about what "empty" means. See utils/profileSections.js for the sibling
 *    rule one layer down (a JSONB column can hold a STRING).
 */

/** The chart sections a facesheet carries, in print order. */
export const FACESHEET_SECTIONS = Object.freeze([
  'identification',
  'insurance',
  'allergies',
  'medications',
  'medicalHistory',
  'familyHistory',
  'socialLifestyle',
])

/**
 * A section as a plain object, or null if it is not one.
 * Accepts an object or a JSON string holding one — a JSONB column can hold a
 * string (D288), and this app has been bitten by exactly that.
 */
export function parseFacesheetSection(raw) {
  if (raw == null) return null
  let v = raw
  if (typeof v === 'string') {
    try { v = JSON.parse(v) } catch { return null }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  return v
}


/**
 * 'present' | 'absent' for one section of a normalised facesheet.
 *
 * ★ Callers must branch on this, never on truthiness of the section itself.
 *   `{}` is truthy and that is the whole bug this module exists for.
 */
export function sectionState(facesheet, key) {
  if (!facesheet || !facesheet.present) return 'absent'
  return facesheet.present[key] === true ? 'present' : 'absent'
}

/** Convenience predicate for the renderer. */
export function isPresent(facesheet, key) {
  return sectionState(facesheet, key) === 'present'
}

function ageFromDob(dob) {
  if (!dob) return null
  try {
    const s = String(dob).trim()
    const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s)
    if (Number.isNaN(d.getTime())) return null
    const now = new Date()
    let age = now.getFullYear() - d.getFullYear()
    const before = now.getMonth() < d.getMonth()
      || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())
    if (before) age--
    return age >= 0 && age < 130 ? age : null
  } catch { return null }
}

const IDENT_STRIP = ['idFront', 'idBack']
const INSURANCE_STRIP = ['cardFront', 'cardBack']

function stripBlobs(section, keys) {
  if (!section) return {}
  const out = { ...section }
  for (const k of keys) delete out[k]
  return out
}

/**
 * Normalise either source into the one shape the renderer accepts.
 *
 * `raw` is the server's `facesheet` block (GET /cases/:id/facesheet, or the
 * block returned alongside a hand-off) OR a profile-store object. Both are
 * accepted BECAUSE THEY ARE THE SAME SHAPE — the API deliberately mirrors the
 * store's section names so this function has one branch, not two.
 *
 * `present` is RECOMPUTED here even when the server sent one, so the renderer's
 * notion of presence can never disagree with the data actually in hand.
 */
export function normalizeFacesheet(raw, opts = {}) {
  const src = raw && typeof raw === 'object' ? raw : {}
  const out = { present: {}, source: opts.source || src.source || 'unknown' }

  for (const key of FACESHEET_SECTIONS) {
    const parsed = parseFacesheetSection(src[key]) || {}
    out[key] = parsed
    out.present[key] = Object.keys(parsed).length > 0
  }

  out.identification = stripBlobs(out.identification, IDENT_STRIP)
  out.insurance = stripBlobs(out.insurance, INSURANCE_STRIP)

  const user = src.user && typeof src.user === 'object' ? src.user : {}
  out.user = {
    firstName: user.firstName || src.firstName || '',
    lastName: user.lastName || src.lastName || '',
    email: user.email || src.email || '',
    phone: user.phone || src.phone || '',
  }

  const id = out.identification
  out.firstName = id.firstName || out.user.firstName || ''
  out.lastName = id.lastName || out.user.lastName || ''
  out.dob = id.dob || ''
  out.sex = id.sex || ''
  // PROD-1167: the ACCOUNT number (users.phone) first; the profile copies are only a fallback.
  out.phone = out.user.phone || opts.userPhone || id.phone || id.mobilePhone || ''
  out.email = id.email || out.user.email || opts.userEmail || ''

  // ★ The age is a NUMBER or it is nothing. The old helper returned the STRING
  //   '?', and the template printed it unguarded as `(?y)` — to a physician.
  const serverAge = typeof src.age === 'number' ? src.age : null
  out.age = serverAge !== null ? serverAge : ageFromDob(out.dob)

  return out
}

/**
 * ★★★ THE EMISSION RULE. A hand-off with no identity is not a document.
 *
 * A name alone is not identity on a clinical document — two patients share a
 * name and one of them is not the person in front of you — so a date of birth
 * is required too. This is the predicate the generator refuses on.
 */
export function facesheetHasIdentity(facesheet) {
  if (!facesheet) return false
  const named = !!((facesheet.firstName || '').trim() || (facesheet.lastName || '').trim())
  const dated = !!(facesheet.dob || '').trim()
  return named && dated
}

/** The printed name, or '' — never the word "Patient" standing in for one. */
export function facesheetName(facesheet) {
  if (!facesheet) return ''
  return [facesheet.firstName, facesheet.lastName].filter(Boolean).join(' ').trim()
}

export class FacesheetUnavailableError extends Error {
  constructor(message) {
    super(message || 'The patient facesheet could not be loaded')
    this.name = 'FacesheetUnavailableError'
    this.code = 'FACESHEET_UNAVAILABLE'
  }
}

// ★★ P2 Release 3 (orella-api P2-4, PROD-215) — A REFUSAL IS NOT AN OUTAGE.
//   GET /cases/<id>/facesheet now runs require_case_access and answers a caller who may not
//   read the case with 403 {'error': 'Not authorized to access this case'}. The callers
//   treated every failure the same way: they built the document from the device's profile
//   store instead. That fallback is right when the server cannot be reached; it is wrong when
//   the server has just said this account may not read this case. Only a 403 is a refusal —
//   a network error, a timeout or a 5xx keep the store fallback exactly as before.
export class FacesheetDeniedError extends Error {
  constructor(message) {
    super(message || 'This case is not available to this account')
    this.name = 'FacesheetDeniedError'
    this.code = 'FACESHEET_DENIED'
  }
}

/** true when a thrown apiFetch error from the facesheet read is the server's refusal. */
export function facesheetReadRefused(err) {
  return !!err && typeof err === 'object' && Number(err.status) === 403
}

export default normalizeFacesheet
